import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, User, Building2, Briefcase, Eye, EyeOff } from "lucide-react";
import { API } from "@/App";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function AuthPage({ setUser }) {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", name: "", profession: "", company_name: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isRegister ? "/api/auth/register" : "/api/auth/login";
      const payload = isRegister ? form : { email: form.email, password: form.password };
      const res = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      setUser(res.data.user);
      toast.success(isRegister ? "Registrazione completata!" : "Accesso effettuato!");
      navigate("/catalogo");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'accesso");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-stone-950 flex" data-testid="auth-page">
      {/* Left panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center bg-stone-900">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(180,160,120,0.06),transparent_70%)]" />
        <div className="relative z-10 text-center px-12">
          <img src="/logo-uol.png" alt="UOL Paper" className="h-20 w-auto mx-auto mb-6" />
          <p className="text-stone-500 text-lg font-light leading-relaxed max-w-md">
            Il simulatore di carte da parati per professionisti del design d'interni
          </p>
        </div>
      </div>

      {/* Right panel - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-stone-500 hover:text-white transition-colors mb-12 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="uppercase tracking-[0.15em] text-xs">Home</span>
          </button>

          <div className="lg:hidden mb-8">
            <img src="/logo-uol.png" alt="UOL Paper" className="h-12 w-auto" />
          </div>

          <h2 className="text-2xl font-light text-white mb-2">
            {isRegister ? "Registrati" : "Accedi"}
          </h2>
          <p className="text-stone-500 text-sm mb-8">
            {isRegister ? "Crea il tuo account per iniziare a simulare" : "Benvenuto, accedi al tuo account"}
          </p>

          {/* Google Login */}
          <Button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full rounded-none border-white/10 text-white hover:bg-white/5 h-12 mb-6 text-sm"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continua con Google
          </Button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-stone-600 uppercase tracking-widest">oppure</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Nome completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-600" />
                    <Input
                      data-testid="name-input"
                      value={form.name}
                      onChange={e => setForm({...form, name: e.target.value})}
                      className="rounded-none bg-transparent border-white/10 text-white pl-10 h-11 focus:border-amber-300/40"
                      placeholder="Mario Rossi"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Professione</Label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-600" />
                      <select
                        data-testid="profession-select"
                        value={form.profession}
                        onChange={e => setForm({...form, profession: e.target.value})}
                        className="w-full h-11 rounded-none bg-transparent border border-white/10 text-white pl-10 pr-3 text-sm focus:border-amber-300/40 appearance-none"
                      >
                        <option value="" className="bg-stone-900">Seleziona</option>
                        <option value="architect" className="bg-stone-900">Architetto</option>
                        <option value="reseller" className="bg-stone-900">Rivenditore</option>
                        <option value="general_contractor" className="bg-stone-900">General Contractor</option>
                        <option value="designer" className="bg-stone-900">Interior Designer</option>
                        <option value="other" className="bg-stone-900">Altro</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Azienda</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-600" />
                      <Input
                        data-testid="company-input"
                        value={form.company_name}
                        onChange={e => setForm({...form, company_name: e.target.value})}
                        className="rounded-none bg-transparent border-white/10 text-white pl-10 h-11 focus:border-amber-300/40"
                        placeholder="Studio XYZ"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-stone-500">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-600" />
                <Input
                  data-testid="email-input"
                  type="email"
                  value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  className="rounded-none bg-transparent border-white/10 text-white pl-10 h-11 focus:border-amber-300/40"
                  placeholder="mario@studio.it"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-stone-500">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-600" />
                <Input
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  className="rounded-none bg-transparent border-white/10 text-white pl-10 pr-11 h-11 focus:border-amber-300/40"
                  placeholder="Min. 6 caratteri"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              data-testid="submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-white text-stone-950 hover:bg-stone-200 h-12 text-sm uppercase tracking-[0.15em]"
            >
              {loading ? "Caricamento..." : isRegister ? "Registrati" : "Accedi"}
            </Button>

            {!isRegister && (
              <div className="text-center">
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => navigate("/forgot-password")}
                  className="text-xs text-stone-500 hover:text-amber-300 transition-colors underline underline-offset-4"
                >
                  Password dimenticata?
                </button>
              </div>
            )}
          </form>

          <p className="mt-6 text-center text-sm text-stone-500">
            {isRegister ? "Hai già un account?" : "Non hai un account?"}{" "}
            <button
              data-testid="toggle-auth-btn"
              onClick={() => setIsRegister(!isRegister)}
              className="text-white hover:text-amber-300 transition-colors underline underline-offset-4"
            >
              {isRegister ? "Accedi" : "Registrati"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
